import type { PvpEndReason, PvpTeamEndedFrame } from '@kenocyber/shared';
import {
  type PvpAbandonRelation,
  type PvpOutcome,
  type PvpResultText,
  pvpAbandonSentence,
} from './result-text';

/**
 * What a player is told when a TEAM match ends.
 *
 * ## Why this is not `pvpResultText` with another argument
 *
 * The duel's function compares `winner` - a SEAT - against the local seat, and
 * a team match has three people on the winning side and none of them is "seat
 * a". Widening the duel's frame to carry either would have made one function
 * answer two questions with a field that is sometimes absent, and the dangerous
 * answer is the quiet one: a `null` seat read as "not me" is a defeat screen
 * shown to a winner.
 *
 * So the two are separate and the duel's is untouched. What they DO share is the
 * shape of the answer ({@link PvpResultText}) and its outcome scale, because a
 * result screen is a result screen and two vocabularies for it would be two
 * stylesheets.
 *
 * ## The comparison is against the AUTHORITATIVE team, and nothing else
 *
 * Not the chair, not the roster's order, not the last hull left on screen, not
 * an interpolated frame. `winnerTeam` is a letter the room decided and
 * `localTeam` is the letter the room put in this client's own `you` - so the six
 * browsers receive one frame and read it six times correctly, three of them as a
 * victory.
 *
 * ## No default branch, for the same reason as the duel
 *
 * An end reason nobody handled would fall through to the nearest case, and the
 * nearest case in a result screen is a victory. `service_failure` in particular -
 * the room admitting it cannot vouch for its own state - must never be shown as
 * somebody winning. The switch is exhaustive and its fallthrough is `never`.
 */

/** Which side this browser is on, as the room named it. Never a guess. */
export type PvpTeamLetter = 'a' | 'b';

/**
 * Who abandoned, as this particular reader should hear it.
 *
 * Three chairs a side means "somebody on my team left" and "I left" are
 * different sentences, so the chair is compared before the team is - and both
 * are compared against what the SERVER put in this client's own `you`, never
 * against a roster position or a hull still on screen.
 *
 * A client missing either half claims nothing. That is not a defensive default
 * dressed up as one: without its own chair it genuinely cannot tell whether the
 * participant named is itself.
 */
function relationTo(
  abandon: PvpTeamEndedFrame['ab'],
  localTeam: PvpTeamLetter | null,
  localParticipantId: string | null,
): PvpAbandonRelation {
  if (abandon === null || localTeam === null || localParticipantId === null) {
    return 'unknown';
  }
  if (abandon.p === localParticipantId) {
    return 'self';
  }
  return abandon.tm === localTeam ? 'ally' : 'opponent';
}

export function pvpTeamResultText(
  result: PvpTeamEndedFrame,
  localTeam: PvpTeamLetter | null,
  localParticipantId: string | null,
): PvpResultText {
  const reason: PvpEndReason = result.reason;
  /*
   * A win needs BOTH: a team the room named, and a team this client is on.
   *
   * `localTeam === null` is a client that never learned its own chair, which is
   * not a loser - it is a client with nothing to compare. It reads every ending
   * as the neutral one below rather than as a defeat.
   */
  const decided = result.winnerTeam !== null && localTeam !== null;
  const won = decided && result.winnerTeam === localTeam;

  switch (reason) {
    case 'death':
      if (!decided) {
        return {
          title: 'PARTIDA ENCERRADA',
          detail: 'A sala encerrou a partida sem declarar uma equipe vencedora.',
          outcome: 'neutral',
        };
      }
      return won
        ? { title: 'VITÓRIA', detail: 'A equipe adversária foi destruída.', outcome: 'win' }
        : { title: 'DERROTA', detail: 'Sua equipe foi destruída.', outcome: 'loss' };

    case 'abandonment': {
      /*
       * The sentence names a PERSON now, and the title still names a side.
       *
       * The old lines said "sua equipe não voltou" to all three losers, which
       * was false for the two who never went anywhere and false again for the
       * one who chose to leave. The winning team's line was wrong the same way:
       * it announced a reconnection failure for somebody who had pressed a
       * button. Both were guesses made from `won`, because `won` was the only
       * authoritative thing the frame carried.
       */
      const detail = pvpAbandonSentence(
        result.ab?.cause ?? null,
        relationTo(result.ab, localTeam, localParticipantId),
      );
      if (!decided) {
        return { title: 'PARTIDA ENCERRADA', detail, outcome: 'neutral' };
      }
      return won
        ? { title: 'VITÓRIA POR ABANDONO', detail, outcome: 'win' }
        : { title: 'DERROTA', detail, outcome: 'loss' };
    }

    case 'mutual_destruction':
      return {
        title: 'EMPATE',
        detail: 'As duas equipes foram destruídas no mesmo tick.',
        outcome: 'draw',
      };

    case 'time_limit':
      return {
        title: 'EMPATE POR TEMPO',
        detail: 'O tempo acabou com as duas equipes de pé.',
        outcome: 'draw',
      };

    case 'cancelled_by_challenger':
      return {
        title: 'PARTIDA CANCELADA',
        detail: 'A sala foi encerrada antes de a partida começar.',
        outcome: 'neutral',
      };

    case 'expired_unaccepted':
      return {
        title: 'CONVITE EXPIRADO',
        detail: 'A sala não foi completada a tempo.',
        outcome: 'neutral',
      };

    case 'service_failure':
      /*
       * NEVER a victory, and never a defeat either.
       *
       * The room could not vouch for its own state, so it named no winning team
       * - and inventing one from whichever side happened to be ahead would be a
       * guess wearing a result's clothes.
       */
      return {
        title: 'FALHA DA PARTIDA',
        detail: 'A sala não pôde concluir a partida. Nenhuma equipe venceu.',
        outcome: 'failure',
      };

    default: {
      /*
       * Unreachable, and enforced by the compiler. A tenth reason added to the
       * contract stops this file compiling, which is the point.
       */
      const unhandled: never = reason;
      throw new Error(`unhandled pvp team end reason: ${String(unhandled)}`);
    }
  }
}

/**
 * Whether the room named a winning TEAM at all.
 *
 * A separate question from which one: a draw and a match with no trustworthy
 * result both have no winner, and only the reason tells them apart.
 */
export function pvpTeamNamesAWinner(result: PvpTeamEndedFrame): boolean {
  return result.winnerTeam !== null;
}

/** Re-exported so a screen needs one import for a result and its colour scale. */
export type { PvpOutcome, PvpResultText };
